import _ from 'lodash';

import { checkIsPageRenderingCancelledException } from 'utils/pdf';
import { checkIsAndroid, checkIsIpadOs, getIsNativePlatform, isTouchPrimary } from 'utils/platform';

import {
  CANVAS_CONTEXT_ATTRIBUTES,
  HALF_TURN_DEGREES,
  MAX_CANVAS_DIMENSION,
  MAX_CANVAS_PIXELS_DESKTOP,
  MAX_CANVAS_PIXELS_MOBILE,
  MAX_PAGE_OUTPUT_SCALE,
  MAX_THUMBNAIL_CANVAS_PIXELS,
  MAX_THUMBNAIL_OUTPUT_SCALE,
  MAX_THUMBNAIL_OUTPUT_SCALE_MOBILE,
  RELEASED_CANVAS_SIZE,
  THUMBNAIL_MAX_CONTENT_WIDTH,
  THUMBNAIL_MIN_CONTENT_HEIGHT,
} from './PdfCard.constants';

export const getPageViewportSize = ({ pageSize, scale, rotation }) => {
  const isQuarterTurn = rotation % HALF_TURN_DEGREES !== 0;

  return {
    width: (isQuarterTurn ? pageSize.height : pageSize.width) * scale,
    height: (isQuarterTurn ? pageSize.width : pageSize.height) * scale,
  };
};

export const getCanvasCssSize = ({ width, height }) => ({
  width: Math.floor(width),
  height: Math.floor(height),
});

export const getCanvasCssBox = size => {
  const { width, height } = getCanvasCssSize(size);

  return { width: `${width}px`, aspectRatio: `${width} / ${height}` };
};

export const applyCanvasCssSize = (canvas, size) => {
  const { width, aspectRatio } = getCanvasCssBox(size);

  canvas.style.width = width;
  canvas.style.aspectRatio = aspectRatio;
};

// The thumbnail max-width / min-height clamps (PdfCard.styles) decide the box actually shown.
export const getThumbnailDisplaySize = ({ width, height }) => {
  if (!width || !height) {
    return { width, height };
  }

  const displayWidth = Math.min(width, THUMBNAIL_MAX_CONTENT_WIDTH);

  return {
    width: displayWidth,
    height: Math.max((height * displayWidth) / width, THUMBNAIL_MIN_CONTENT_HEIGHT),
  };
};

export const checkIsMobileCanvasPlatform = () =>
  getIsNativePlatform() || isTouchPrimary() || checkIsIpadOs() || checkIsAndroid();

export const getMaxCanvasPixels = ({ isMobileCanvasBudget }) =>
  isMobileCanvasBudget ? MAX_CANVAS_PIXELS_MOBILE : MAX_CANVAS_PIXELS_DESKTOP;

const getMaxOutputScale = ({ isThumbnail, isMobileCanvasPlatform }) => {
  if (!isThumbnail) {
    return MAX_PAGE_OUTPUT_SCALE;
  }

  return isMobileCanvasPlatform ? MAX_THUMBNAIL_OUTPUT_SCALE_MOBILE : MAX_THUMBNAIL_OUTPUT_SCALE;
};

// pdf.js PDFPageView parity: a DPR below 1 (browser zoom-out) still renders at 1x, but the pixel
// budget may take the scale below 1x, so an oversized page renders soft instead of blank.
export const getCanvasOutputScale = ({
  cssWidth,
  cssHeight,
  devicePixelRatio,
  maxCanvasPixels,
  maxOutputScale,
}) =>
  Math.min(
    Math.max(devicePixelRatio, 1),
    maxOutputScale,
    Math.sqrt(maxCanvasPixels / (cssWidth * cssHeight)),
    MAX_CANVAS_DIMENSION / cssWidth,
    MAX_CANVAS_DIMENSION / cssHeight,
  );

const getCanvasBackingSide = (cssSide, outputScale) =>
  cssSide > 0 ? Math.max(Math.floor(cssSide * outputScale), 1) : 0;

export const getCanvasBackingSize = ({ cssWidth, cssHeight, outputScale }) => ({
  width: getCanvasBackingSide(cssWidth, outputScale),
  height: getCanvasBackingSide(cssHeight, outputScale),
});

// pdf.js applies this before viewport.transform, so the viewport stays in CSS px.
export const getCanvasRenderTransform = ({ cssSize, backingSize }) => {
  const isCssSized = backingSize.width === cssSize.width && backingSize.height === cssSize.height;

  if (isCssSized || !cssSize.width || !cssSize.height) {
    return null;
  }

  return [backingSize.width / cssSize.width, 0, 0, backingSize.height / cssSize.height, 0, 0];
};

// At DPR <= 1 a thumbnail rasterizes its declared box untransformed, as before HiDPI support; the
// clamped display box only sizes the raster once the device ratio adds pixels.
export const getCanvasRenderTarget = ({ cssSize, isThumbnail, devicePixelRatio }) => {
  const isMobileCanvasPlatform = checkIsMobileCanvasPlatform();
  const isDisplayBoxSized = isThumbnail && devicePixelRatio > 1;
  const { width, height } = isDisplayBoxSized ? getThumbnailDisplaySize(cssSize) : cssSize;
  const outputScale = getCanvasOutputScale({
    cssWidth: width,
    cssHeight: height,
    devicePixelRatio,
    maxCanvasPixels: isThumbnail
      ? MAX_THUMBNAIL_CANVAS_PIXELS
      : getMaxCanvasPixels({ isMobileCanvasBudget: isMobileCanvasPlatform }),
    maxOutputScale: getMaxOutputScale({ isThumbnail, isMobileCanvasPlatform }),
  });
  const backingSize = getCanvasBackingSize({ cssWidth: width, cssHeight: height, outputScale });

  return { backingSize, transform: getCanvasRenderTransform({ cssSize, backingSize }) };
};

const offscreenPaintQueue = { tail: Promise.resolve() };

// Retained offscreen pages repaint one at a time. A zoom or density step otherwise allocates and
// rasterizes up to ten budget-sized canvases at once (a 280 ms long task at 300% zoom on DPR 3).
export const queueOffscreenPaint = paint => {
  const run = offscreenPaintQueue.tail.then(paint);

  offscreenPaintQueue.tail = run.catch(_.noop);

  return run;
};

// A released canvas keeps its last key but not its raster, so its bitmap no longer has the painted
// size. The document is compared by identity: _.isEqual would deep-walk a reloaded PDFDocumentProxy.
export const checkIsCanvasPaintCurrent = ({ canvas, paintedKey, paintKey }) =>
  !_.isNull(paintedKey) &&
  canvas.width === paintKey.backingSize.width &&
  canvas.height === paintKey.backingSize.height &&
  paintedKey.pdf === paintKey.pdf &&
  _.isEqual(_.omit(paintedKey, 'pdf'), _.omit(paintKey, 'pdf'));

export const resizeCanvasBackingStore = (canvas, { width, height }) => {
  const nextWidth = Math.floor(width);
  const nextHeight = Math.floor(height);

  if (canvas.width !== nextWidth) {
    canvas.width = nextWidth;
  }

  if (canvas.height !== nextHeight) {
    canvas.height = nextHeight;
  }
};

// An opaque (alpha: false) canvas reads black after a resize until pdf.js starts drawing, which
// flickers during fast scrolls. save/restore keeps fillStyle: pdf.js restores the caller's
// fillStyle after its own background fill and relies on the default black for text.
export const paintCanvasBackground = ({ canvas, context, color }) => {
  context.save();
  context.fillStyle = color;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.restore();
};

export const releaseCanvasBackingStore = canvas => {
  if (!canvas) {
    return;
  }

  resizeCanvasBackingStore(canvas, { width: 0, height: 0 });
};

// Chrome draws an opaque (alpha: false) canvas with an empty bitmap as a black box over its CSS
// paper, and a released page can scroll into view before its repaint starts, so a released page
// keeps a 1x1 bitmap in the paper colour. A canvas never painted has no context and shows the paper.
export const releaseCanvasToPaper = ({ canvas, color }) => {
  if (!canvas || canvas.width === 0) {
    return;
  }

  resizeCanvasBackingStore(canvas, RELEASED_CANVAS_SIZE);
  paintCanvasBackground({
    canvas,
    context: canvas.getContext('2d', CANVAS_CONTEXT_ATTRIBUTES),
    color,
  });
};

// A superseded run, a cancelled task, or a destroyed document (its in-flight getPage/render
// rejects with "Transport destroyed" / "Page was destroyed.") is expected teardown, not a failure.
export const checkIsRenderErrorSilent = ({ error, isStale, pdf }) =>
  isStale || checkIsPageRenderingCancelledException(error) || !!pdf?.loadingTask?.destroyed;
